import React, { useState, useEffect, useMemo } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from 'reactstrap'
import Select from 'react-select'
import { useMutation, useQuery } from '@apollo/client'
import { ADD_INBOX_MEMBER, UPDATE_INBOX_GROUP_ACCESS, UPDATE_INBOX_MEMBER } from './Mutations'
import { GET_ADMIN_INBOXES } from './Queries'
import { GroupsTypeAhead, RepsTypeAhead } from '../../TaskManagement/TaskComponents/TypeAheadComponents'

const MEMBER_ROLES = [
    { value: 'admin', label: 'Admin' },
    { value: 'member', label: 'Member' }
]

export default function AddEditInboxMemberModal({ isOpen, toggle, inboxMember, inboxGroupAccess, onSuccess }) {
    const [inboxId, setInboxId] = useState('')
    const [userIds, setUserIds] = useState([])
    const [groupIds, setGroupIds] = useState([])
    const [role, setRole] = useState('member')
    const [canBeAssigned, setCanBeAssigned] = useState(true)
    const [errors, setErrors] = useState([])

    const isEditMember = !!inboxMember
    const isEditGroupAccess = !!inboxGroupAccess
    const isEdit = isEditMember || isEditGroupAccess

    const { data: inboxesData } = useQuery(GET_ADMIN_INBOXES, {
        variables: { isArchived: false }
    })

    const inboxes = useMemo(
        () => inboxesData?.myAdminInboxes?.edges?.map(edge => edge.node) || [],
        [inboxesData]
    )

    useEffect(() => {
        if (inboxMember) {
            setInboxId(inboxMember.inbox?.id || '')
            setRole(inboxMember.role || 'member')
            setCanBeAssigned(inboxMember.canBeAssigned !== false)
        } else if (inboxGroupAccess) {
            setInboxId(inboxGroupAccess.inbox?.id || '')
            setRole(inboxGroupAccess.role || 'member')
            setCanBeAssigned(inboxGroupAccess.canBeAssigned !== false)
        } else {
            setInboxId(inboxes.length > 0 ? inboxes[0].id : '')
            setUserIds([])
            setGroupIds([])
            setRole('member')
            setCanBeAssigned(true)
        }
        setErrors([])
    }, [inboxGroupAccess, inboxMember, isOpen, inboxes])

    const [addInboxMember, { loading: adding }] = useMutation(ADD_INBOX_MEMBER, {
        onCompleted: (data) => {
            if (data.messagingAddInboxMember.ok) {
                toggle()
                if (onSuccess) {
                    onSuccess(`Inbox access added.`)
                }
            } else {
                setErrors(data.messagingAddInboxMember.errors || ['Failed to add member'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const [updateInboxMember, { loading: updating }] = useMutation(UPDATE_INBOX_MEMBER, {
        onCompleted: (data) => {
            if (data.messagingUpdateInboxMember.ok) {
                toggle()
                if (onSuccess) {
                    const updatedMember = data.messagingUpdateInboxMember.inboxMember
                    const displayName = updatedMember?.user?.username || 'Member'
                    onSuccess(`${displayName}'s permissions have been updated.`)
                }
            } else {
                setErrors(data.messagingUpdateInboxMember.errors || ['Failed to update member'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const [updateInboxGroupAccess, { loading: updatingGroupAccess }] = useMutation(UPDATE_INBOX_GROUP_ACCESS, {
        onCompleted: (data) => {
            if (data.messagingUpdateInboxGroupAccess.ok) {
                toggle()
                if (onSuccess) {
                    const updatedAccess = data.messagingUpdateInboxGroupAccess.inboxGroupAccess
                    const displayName = updatedAccess?.group?.name || 'Group'
                    onSuccess(`${displayName}'s inbox access has been updated.`)
                }
            } else {
                setErrors(data.messagingUpdateInboxGroupAccess.errors || ['Failed to update inbox group access'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        setErrors([])

        if (isEditMember) {
            updateInboxMember({
                variables: {
                    id: inboxMember.id,
                    role,
                    canBeAssigned
                }
            })
        } else if (isEditGroupAccess) {
            updateInboxGroupAccess({
                variables: {
                    id: inboxGroupAccess.id,
                    role,
                    canBeAssigned
                }
            })
        } else {
            if (!inboxId) {
                setErrors(['Please select an inbox'])
                return
            }
            if (userIds.length === 0 && groupIds.length === 0) {
                setErrors(['Please select at least one user or group'])
                return
            }

            addInboxMember({
                variables: {
                    inboxId,
                    userIds,
                    groupIds,
                    role,
                    canBeAssigned
                }
            })
        }
    }

    const loading = adding || updating || updatingGroupAccess

    return (
        <Modal isOpen={isOpen}>
            <ModalHeader>
                {isEdit ? 'Edit Inbox Access' : 'Add Inbox Access'}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {errors.length > 0 && (
                        <Alert color="danger">
                            {errors.map((error, idx) => (
                                <div key={idx}>{error}</div>
                            ))}
                        </Alert>
                    )}

                    {!isEdit && (
                        <>
                            <FormGroup>
                                <Label for="memberInbox">Inbox *</Label>
                                <Select className="bos-custom-select"
                                    classNamePrefix="bos-select"
                                    isLoading={false}
                                    options={
                                        inboxes.map((inbox) => ({ value: inbox.id, label: inbox.name }))
                                    }
                                    placeholder="Select inbox for channel"
                                    isMulti={false}
                                    value={inboxes
                                        .map((inbox) => ({ value: inbox.id, label: inbox.name }))
                                        .find((option) => option.value === inboxId) || null}
                                    onChange={(option) => setInboxId(option.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="userIds">Users to add</Label>
                                <RepsTypeAhead defaultValue={userIds} onChange={(selectedUserIds) => { setUserIds(selectedUserIds) }} />
                            </FormGroup>

                            <FormGroup>
                                <Label for="groupIds">Groups to add</Label>
                                <GroupsTypeAhead defaultValue={groupIds} onChange={(selectedGroupIds) => { setGroupIds(selectedGroupIds) }} />
                                <small className="text-muted d-block mt-2">
                                    Members of the selected groups will inherit this inbox access automatically.
                                </small>
                            </FormGroup>
                        </>
                    )}

                    {isEditMember && (
                        <FormGroup>
                            <Label>User</Label>
                            <Input
                                type="text"
                                value={`${inboxMember.user?.username || ''} (${inboxMember.user?.email || ''})`}
                                disabled
                            />
                        </FormGroup>
                    )}

                    {isEditGroupAccess && (
                        <FormGroup>
                            <Label>Group</Label>
                            <Input
                                type="text"
                                value={inboxGroupAccess.group?.name || ''}
                                disabled
                            />
                        </FormGroup>
                    )}

                    <FormGroup>
                        <Label for="memberRole">Role *</Label>
                        <Select className="bos-custom-select"
                            classNamePrefix="bos-select"
                            isLoading={false}
                            options={MEMBER_ROLES}
                            placeholder="Select member role"
                            isMulti={false}
                            value={MEMBER_ROLES.find(r => r.value === role)}
                            onChange={(option) => setRole(option.value)}
                        />
                    </FormGroup>

                    <FormGroup check>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={canBeAssigned}
                                onChange={(e) => setCanBeAssigned(e.target.checked)}
                                disabled={loading}
                            />
                            Can be assigned conversations
                        </Label>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                        {loading ? 'Saving...' : isEdit ? 'Update' : 'Add'}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}
